import Field from '@/gf/components/Field'
import AccountMachineM from '@/gf/modules/AccountMachine'
import { XCircleIcon } from '@heroicons/react/24/outline'
import React from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'

import AddAccountMachineModal from '@/gf/components/AddAccountMachineModal'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Tooltip from '@/gf/components/Tooltip'
import useToggle from '@/gf/hooks/useToggle'
import { AccountMachine, Machine } from '../../../types'
import SupplierAutocomplete from '../../components/SupplierAutocomplete'
import useAllAccountMachines, { SEARCH_ACCOUNT_MACHINES } from '../../hooks/useAllAccountMachines'

import { CreateRfqForm } from './form'

interface SelectedAccountMachine {
  id: string
  name: string
  serialNumber: string | null
  description: string
  machine: Machine
}

const AddMachineButton = ({
  accountId,
  selectedMachine,
  onAddMachine,
}: {
  accountId: string
  selectedMachine: SelectedAccountMachine | undefined
  onAddMachine: () => void
}) => {
  if (accountId === '') {
    return (
      <div className="flex flex-row items-center space-x-2">
        <Tooltip left text="Must select user before adding machine">
          <Button
            primary={false}
            className="text-opacity-75 hover:cursor-not-allowed"
            onClick={(e) => e?.stopPropagation()}
          >
            Add Machine
          </Button>
        </Tooltip>
      </div>
    )
  }
  if (!selectedMachine) {
    return (
      <Button onClick={onAddMachine} primary={false}>
        Add Machine
      </Button>
    )
  }
  return null
}

const AdminMachineCard = ({
  accountId,
  createRfqForm,
  shouldValidate,
}: {
  accountId: string
  createRfqForm: UseFormReturn<CreateRfqForm>
  shouldValidate: (formStateErrors: Record<string, unknown>) => boolean
}) => {
  const [addMachineModalOpen, addMachineModalToggler] = useToggle()
  const [selectedOrgMachine, setSelectedOrgMachine] = React.useState<SelectedAccountMachine>()

  const selectedMachineId = useWatch({ control: createRfqForm.control, name: 'accountMachineId' })
  const selectedMachine = selectedMachineId ? selectedOrgMachine : undefined

  const allOrgMachines = useAllAccountMachines(accountId)
  const [allAccountMachines, setAllAccountMachines] = React.useState<AccountMachine[]>([])

  const accountMachineOptions = allOrgMachines.map((om) => ({
    value: om.id,
    label: `${om.name}, ${om.machine.make}, ${om.machine.model}, ${om.serialNumber}`,
  }))

  const selectMachine = (accountMachineId: string) => {
    const accountMachine = allOrgMachines.find((om) => om.id === accountMachineId)
    setSelectedOrgMachine(accountMachine)
    createRfqForm.setValue('accountMachineId', accountMachineId, {
      shouldValidate: shouldValidate(createRfqForm.formState.errors),
    })
  }

  return (
    <>
      <AddAccountMachineModal
        open={addMachineModalOpen}
        onClose={addMachineModalToggler.off}
        onAdd={(accountMachine) => {
          if (accountMachine) {
            setSelectedOrgMachine(accountMachine)
            createRfqForm.setValue('accountMachineId', accountMachine.id, {
              shouldValidate: shouldValidate(createRfqForm.formState.errors),
            })
          }
        }}
        accountId={accountId}
      />
      <Card
        title="Machine"
        headerButton={
          <AddMachineButton
            accountId={accountId}
            selectedMachine={selectedMachine}
            onAddMachine={addMachineModalToggler.on}
          />
        }
      >
        <Card.Section>
          {selectedMachine ? (
            <div key={selectedMachine.id}>
              <div key={selectedMachine.id} className="flex w-full items-center justify-between">
                <div className="text-left block">
                  <p className="text-small capitalize">unit # {selectedMachine.name}</p>
                </div>
                <XCircleIcon
                  className="h-6 w-6 cursor-pointer text-gray-400 hover:text-red-400"
                  onClick={() => {
                    createRfqForm.setValue('accountMachineId', undefined, {
                      shouldValidate: shouldValidate(createRfqForm.formState.errors),
                    })
                  }}
                />
              </div>
              <div className="mt-2 pt-2 border-t-1 first:mt-0 first:pt-0 first:border-t-0 text-sm">
                <div className="leading-5 capitalize">
                  serial number {selectedMachine.serialNumber}
                </div>
                <div className="leading-5 capitalize">make {selectedMachine.machine.make}</div>
                <div className="leading-5 capitalize">model {selectedMachine.machine.model}</div>
                <div className="leading-5 capitalize">year {selectedMachine.machine.year}</div>
              </div>
            </div>
          ) : (
            <Field label="" errors={createRfqForm.formState.errors?.accountMachineId?.message}>
              <SupplierAutocomplete
                defaultValue={null}
                defaultOptions={accountMachineOptions}
                allData={allAccountMachines}
                query={SEARCH_ACCOUNT_MACHINES}
                selected={[]}
                variables={{ organizationId: accountId }}
                reactSelectOptions={{ isMulti: false }}
                onChange={(option) => selectMachine(option?.value)}
                transformation={(response) => {
                  const orgMachines = response.data.searchAccountMachines.map((om) => ({
                    value: om.id,
                    label: AccountMachineM.getSearchDisplayNameFromPayload(om),
                  }))

                  setAllAccountMachines(orgMachines)

                  return orgMachines
                }}
              />
            </Field>
          )}
        </Card.Section>
      </Card>
    </>
  )
}

export default AdminMachineCard
